import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { selectDefaultPhoneCountry } from '@common-reducers/UserSelectors';
import { DBSessionThunks } from '@common/reducers/DBServiceThunks';
import { refreshSessions } from '../refresh-sessions';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { useIsMobile } from '@common/hooks/use-is-mobile';
import BtMUIValidatedPhone from '@common-components/bt-mui-validated-phone/bt-mui-validated-phone';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import QRCode from 'react-qr-code';
import { Check, Copy, Loader2, QrCode, RotateCcw, Smartphone, TimerOff } from 'lucide-react';
import { toast } from 'sonner';
import { DisplayEngine } from '../types';
import { derivePairingStep, deriveQrStep } from '../pairing-connect';

type ConnectMethod = 'qr' | 'phone';

// FE#302: the pairing-code ("link with phone number") path is HIDDEN on prod until the Baileys
// pod-side bridge is built + canary-validated. The FE writes {linkMethod:'pairing_code',
// phoneNumber} to the session, but no pod consumer requests the code / writes pairingCode back
// yet, so the flow dead-ends ("requesting your code…" spins forever). Flip to `true` only once
// the bridge ships and the channel is canary-proven. Until then the dialog offers QR only.
const PAIRING_CODE_ENABLED = true;

interface ConnectGatewayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected engine. Its `.session` carries the live status / pairingCode the flow reads. */
  engine: DisplayEngine | undefined;
  /** Which method to land on when opened. Defaults to QR on desktop, phone-linking on mobile. */
  initialMethod?: ConnectMethod;
  /** The server removed this engine while the dialog was open (QR attempts ran out). */
  expired?: boolean;
  /** Create a fresh connection (and reopen this flow on it). */
  onStartOver?: () => void;
  /** Render the flow in place (no dialog, no title) — the host page owns the heading and moves on
   *  by itself once connected, so the success screen is not shown either. */
  inline?: boolean;
}

/** Baileys rotates the QR every ~20s (60s for the first); one unchanged longer than this is dead. */
const QR_FROZEN_MS = 75_000;

/**
 * Segmented-control pill. The two segments sit inside a padded track (see the toggle markup),
 * so the active one reads as a raised pill rather than a full-bleed blue block. Labels are
 * kept on ONE line (nowrap + ellipsis) — a wrapping label was what made the control look
 * cramped and knocked the two segments to different heights.
 */
const segStyle = (active: boolean): React.CSSProperties => ({
  flex: 1,
  minWidth: 0,
  height: 40,
  padding: '0 10px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  border: 'none',
  borderRadius: 8,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  transition: 'background-color 0.15s, color 0.15s, box-shadow 0.15s',
  backgroundColor: active ? 'var(--cta-button-bg)' : 'transparent',
  color: active ? '#ffffff' : 'var(--color-text-muted)',
  boxShadow: active ? '0 1px 3px rgba(0, 0, 0, 0.18)' : 'none',
});

const segLabelStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/**
 * The shared BtMUIValidatedPhone spreads `restProps` AFTER its own `sx`, so passing `sx` REPLACES
 * the component default — width/colours have to be restated here. Selectors are direct-child
 * scoped so they hit the phone TextField only and not the country <Select> nested in its
 * start-adornment (which renders its own OutlinedInput).
 */
const phoneFieldSx = {
  width: '100%',
  '& > .MuiOutlinedInput-root': {
    minHeight: 48,
    borderRadius: '10px',
    backgroundColor: 'var(--color-surface)',
    color: 'var(--color-text)',
  },
  // borderWidth must be explicit: Tailwind's preflight (`*, ::before, ::after { border-width: 0 }`)
  // flattens MUI's fieldset border, which is why this field rendered with no visible box at all.
  '& > .MuiOutlinedInput-root > .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-border)',
    borderWidth: '1px',
    borderStyle: 'solid',
  },
  '& > .MuiOutlinedInput-root:hover > .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--color-text-muted)',
    borderWidth: '1px',
  },
  '& > .MuiOutlinedInput-root.Mui-focused > .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--cta-button-bg)',
    borderWidth: '1.5px',
  },
  '& input': { color: 'var(--color-text)' },
  '& > .MuiFormHelperText-root': {
    marginInlineStart: '2px',
    marginTop: '6px',
    fontSize: '0.75rem',
    color: 'var(--color-text-muted)',
  },
};

export const ConnectGatewayDialog: React.FC<ConnectGatewayDialogProps> = ({
  open,
  onOpenChange,
  engine,
  initialMethod,
  expired = false,
  onStartOver,
  inline = false,
}) => {
  const { t } = useTranslation(namespace);
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const defaultCountry = useAppSelector(selectDefaultPhoneCountry) || 'us';
  const [updateSession] = backendApi.useUpdateSessionMutation();

  const session = engine?.session;
  const sessionId: string | undefined = session?._id;
  const status: string = session?.status ?? '';
  const qrCode: string = engine?.qrCode || '';

  const [method, setMethod] = useState<ConnectMethod>(PAIRING_CODE_ENABLED ? 'phone' : 'qr');
  const [phone, setPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(false);
  const [phoneSubmitted, setPhoneSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset local state each time the dialog opens; default the method (QR is unusable on a phone,
  // where WhatsApp and the browser share one device — so mobile lands on phone-linking).
  useEffect(() => {
    if (open) {
      setMethod(PAIRING_CODE_ENABLED ? (initialMethod ?? (isMobile ? 'phone' : 'qr')) : 'qr');
      setPhone('');
      setPhoneValid(false);
      setPhoneSubmitted(false);
      setSubmitting(false);
      setCopied(false);
    }
  }, [open, initialMethod, isMobile]);

  // While the connect dialog is open and not yet connected, poll the session so status /
  // pairingCode transitions (written by the pod) reach the store even without an MQTT push.
  // Fetch once immediately on open: the row can be minutes stale, and waiting a full interval
  // is what left the dialog on a spinner while a QR was already sitting on the server.
  // refreshSessions (not a bare find) so a session the server reaped actually leaves the store —
  // that is what flips this dialog to its "expired" state.
  useEffect(() => {
    if (!open || !sessionId || status === 'connected' || expired) return;
    dispatch(refreshSessions());
    const id = setInterval(() => {
      dispatch(refreshSessions());
    }, 2500);
    return () => clearInterval(id);
  }, [open, sessionId, status, expired, dispatch]);

  const step = useMemo(() => derivePairingStep(session, phoneSubmitted), [session, phoneSubmitted]);

  // qrSeen: a QR was on screen during this open. After a scan the pod clears the QR while it
  // links; this is what lets that gap read as "Scanned! Linking…" instead of "Preparing…".
  const [qrSeen, setQrSeen] = useState(false);
  useEffect(() => {
    if (open && status === 'qrcode' && qrCode) setQrSeen(true);
  }, [open, status, qrCode]);
  useEffect(() => {
    if (open) setQrSeen(false);
  }, [open]);
  const qrStep = useMemo(() => deriveQrStep(session, qrCode, qrSeen), [session, qrCode, qrSeen]);
  const isConnected = status === 'connected' && !expired;

  // Frozen-QR guard: if the code on screen stops rotating, it has expired on WhatsApp's side even
  // if nothing told us. Blur it rather than let someone scan a dead code.
  const [qrFrozen, setQrFrozen] = useState(false);
  useEffect(() => {
    setQrFrozen(false);
    if (!open || !qrCode) return;
    const id = setTimeout(() => setQrFrozen(true), QR_FROZEN_MS);
    return () => clearTimeout(id);
  }, [open, qrCode]);

  // A pod that never produces a QR must not leave the user on an endless spinner: after a
  // generous boot window, say so and offer a restart right here.
  const [startingSlow, setStartingSlow] = useState(false);
  useEffect(() => {
    setStartingSlow(false);
    if (!open || qrStep !== 'starting') return;
    const id = setTimeout(() => setStartingSlow(true), 150_000);
    return () => clearTimeout(id);
  }, [open, qrStep]);

  const [restartK8sEngine] = backendApi.useRestartK8sEngineMutation();
  const [restarting, setRestarting] = useState(false);
  const handleRestart = useCallback(async () => {
    if (!sessionId || restarting) return;
    setRestarting(true);
    try {
      await restartK8sEngine(sessionId).unwrap();
      await updateSession({ id: sessionId, status: 'initializing', lastRestart: new Date().toISOString() }).unwrap();
      setStartingSlow(false);
      setQrSeen(false);
      dispatch(DBSessionThunks.find({}));
    } catch {
      toast.error(t('enginesManagement.restartEngineError'));
    } finally {
      setRestarting(false);
    }
  }, [sessionId, restarting, restartK8sEngine, updateSession, dispatch, t]);

  const handlePhoneChange = useCallback((value: string, valid: boolean) => {
    setPhone(value);
    setPhoneValid(valid);
  }, []);

  const handleSubmitPhone = useCallback(async () => {
    if (!sessionId || !phoneValid || submitting) return;
    setSubmitting(true);
    try {
      // Session-document-driven: the pod's poll loop reads linkMethod + phoneNumber and starts the
      // pairing-code flow, writing back status → pairing_code + the 8-digit `pairingCode`.
      await updateSession({ id: sessionId, linkMethod: 'pairing_code', phoneNumber: phone }).unwrap();
      setPhoneSubmitted(true);
      dispatch(DBSessionThunks.find({}));
    } catch (error) {
      toast.error(t('enginesManagement.pairing.submitError', { defaultValue: 'Could not request a code. Check the number and try again.' }));
    } finally {
      setSubmitting(false);
    }
  }, [sessionId, phoneValid, phone, submitting, updateSession, dispatch, t]);

  const handleTryAnotherNumber = useCallback(() => {
    setPhoneSubmitted(false);
    setPhone('');
    setPhoneValid(false);
  }, []);

  const handleCopyCode = useCallback(async () => {
    if (!session?.pairingCode) return;
    try {
      await navigator.clipboard.writeText(session.pairingCode.replace(/[^A-Z0-9]/gi, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the code is visible on screen regardless */
    }
  }, [session?.pairingCode]);

  const instructions = (
    <ol className="text-sm text-left space-y-1.5" style={{ color: 'var(--color-text-muted)', paddingInlineStart: '1.1rem', listStyle: 'decimal' }}>
      <li>{t('enginesManagement.pairing.step1', { defaultValue: 'Open WhatsApp on your phone' })}</li>
      <li>{t('enginesManagement.pairing.step2', { defaultValue: 'Tap Settings → Linked Devices → Link a Device' })}</li>
      <li>{t('enginesManagement.pairing.step3', { defaultValue: 'Tap "Link with phone number instead"' })}</li>
      <li>{t('enginesManagement.pairing.step4', { defaultValue: 'Enter the code shown above' })}</li>
    </ol>
  );

  const renderPhonePanel = () => {
    switch (step) {
      case 'show_code':
        return (
          <div className="flex flex-col items-center text-center gap-4">
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {t('enginesManagement.pairing.codeIntro', { defaultValue: 'Enter this code in WhatsApp on your phone:' })}
            </p>
            <button
              type="button"
              onClick={handleCopyCode}
              className="flex items-center gap-3 rounded-xl px-5 py-3"
              style={{ backgroundColor: 'var(--color-surface-accent)', border: '1px solid var(--color-border)' }}
              aria-label={t('enginesManagement.pairing.copyCode', { defaultValue: 'Copy code' })}
            >
              <span
                className="font-mono font-bold tracking-[0.25em]"
                style={{ fontSize: '2rem', color: 'var(--color-text)' }}
              >
                {session?.pairingCode}
              </span>
              {copied ? <Check size={18} style={{ color: 'var(--status-success)' }} /> : <Copy size={18} style={{ color: 'var(--color-text-muted)' }} />}
            </button>
            {session?.phoneNumber && (
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {/* The phone is wrapped in an LTR ISOLATE (U+2066 … U+2069). Without it the
                    leading "+" is a bidi-neutral sitting between Hebrew text and the digits, so
                    it resolves to the paragraph direction and renders on the WRONG END:
                    "+972501234567" came out as "972501234567+" on the very screen where the
                    user checks that this is their own number. */}
                {t('enginesManagement.pairing.linkingFor', {
                  defaultValue: 'Linking {{phone}}',
                  phone: `⁦${session.phoneNumber}⁩`,
                })}
              </p>
            )}
            {instructions}
            <p className="text-xs flex items-center gap-1" style={{ color: 'var(--color-text-muted)' }}>
              <Loader2 className="animate-spin" size={12} />
              {t('enginesManagement.pairing.waitingForLink', { defaultValue: 'Waiting for you to enter the code…' })}
            </p>
          </div>
        );

      case 'requesting':
        return (
          <div className="flex flex-col items-center text-center py-8 gap-3">
            <Loader2 className="animate-spin" size={32} style={{ color: 'var(--cta-button-bg)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
              {t('enginesManagement.pairing.requestingTitle', { defaultValue: 'Requesting your code…' })}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {t('enginesManagement.pairing.requestingBody', { defaultValue: 'This can take up to a minute while we start your connection.' })}
            </p>
          </div>
        );

      case 'error':
        return (
          <div className="flex flex-col items-center text-center py-6 gap-3">
            <p className="text-sm font-medium" style={{ color: 'var(--status-error)' }}>
              {t('enginesManagement.pairing.errorTitle', { defaultValue: 'We could not create a code' })}
            </p>
            {/* The translated, ACTIONABLE sentence is the error message. This used
                to prefer `session.pairingCodeError` — the raw string the gateway
                pod wrote — so a Hebrew reader met an English machine string
                ("Bad request", "connection closed") in place of the one line that
                tells them what to do about it. The raw string is still shown, but
                as what it is: a technical detail underneath, for support. */}
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              {t('enginesManagement.pairing.errorBody', { defaultValue: 'Please check the number is correct and try again.' })}
            </p>
            {session?.pairingCodeError && (
              <p className="text-[11px] font-mono" style={{ color: 'var(--color-text-muted)', opacity: 0.75 }} dir="ltr">
                {session.pairingCodeError}
              </p>
            )}
            <Button variant="outline" onClick={handleTryAnotherNumber}>
              {t('enginesManagement.pairing.tryAnotherNumber', { defaultValue: 'Try another number' })}
            </Button>
          </div>
        );

      case 'enter_phone':
      default:
        return (
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
              {t('enginesManagement.pairing.enterPhoneBody', { defaultValue: 'Enter your WhatsApp phone number with the country code. We\'ll give you a code to type into WhatsApp.' })}
            </p>
            <BtMUIValidatedPhone
              value={phone}
              countryCode={defaultCountry}
              onChange={handlePhoneChange}
              sx={phoneFieldSx}
            />
            <Button
              className="w-full text-white font-semibold"
              style={{ backgroundColor: 'var(--cta-button-bg)', height: 44, borderRadius: 10 }}
              disabled={!phoneValid || submitting}
              onClick={handleSubmitPhone}
            >
              {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : <Smartphone className="mr-2" size={16} />}
              {t('enginesManagement.pairing.getCode', { defaultValue: 'Get code' })}
            </Button>
          </div>
        );
    }
  };

  const renderConnected = () => (
    <div className="flex flex-col items-center text-center py-4 gap-3">
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 72, height: 72, backgroundColor: 'rgba(37, 174, 96, 0.14)', animation: 'bt-pop-in 320ms ease-out' }}
      >
        <Check size={38} strokeWidth={2.5} style={{ color: 'var(--status-success)' }} />
      </div>
      <div className="text-xl font-semibold" style={{ color: 'var(--color-text)' }}>
        {t('enginesManagement.pairing.connectedHeadline', { defaultValue: 'WhatsApp connected' })}
      </div>
      <p className="text-sm leading-relaxed max-w-[320px]" style={{ color: 'var(--color-text-muted)' }}>
        {t('enginesManagement.pairing.connectedDetail', { defaultValue: 'Your remote connection is live. Messages will send even when your computer is off.' })}
      </p>
      <Button
        className="mt-2 w-full text-white font-semibold"
        style={{ backgroundColor: 'var(--cta-button-bg)', height: 44, borderRadius: 10 }}
        onClick={() => onOpenChange(false)}
      >
        {t('enginesManagement.pairing.done', { defaultValue: 'Done' })}
      </Button>
    </div>
  );

  const renderExpired = () => (
    <div className="flex flex-col items-center text-center py-4 gap-3">
      <div
        className="flex items-center justify-center rounded-full"
        style={{ width: 64, height: 64, backgroundColor: 'rgba(245, 158, 11, 0.14)' }}
      >
        <TimerOff size={30} style={{ color: 'var(--status-warning)' }} />
      </div>
      <div className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>
        {t('enginesManagement.pairing.expiredTitle', { defaultValue: 'This QR code expired' })}
      </div>
      <p className="text-sm leading-relaxed max-w-[330px]" style={{ color: 'var(--color-text-muted)' }}>
        {t('enginesManagement.pairing.expiredBody', { defaultValue: 'No scan came in time, so nothing was linked. Get a new code and scan it within a couple of minutes.' })}
      </p>
      {onStartOver && (
        <Button
          className="mt-2 w-full text-white font-semibold"
          style={{ backgroundColor: 'var(--cta-button-bg)', height: 44, borderRadius: 10 }}
          onClick={onStartOver}
        >
          <QrCode className="me-2" size={16} />
          {t('enginesManagement.pairing.expiredCta', { defaultValue: 'Get a new QR code' })}
        </Button>
      )}
    </div>
  );

  const qrStepIndex =qrStep === 'starting' ? 0 : qrStep === 'linking' ? 2 : 1;
  const qrStepLabels = [
    t('enginesManagement.pairing.qrStepPrepare', { defaultValue: 'Prepare' }),
    t('enginesManagement.pairing.qrStepScan', { defaultValue: 'Scan' }),
    t('enginesManagement.pairing.qrStepLink', { defaultValue: 'Link' }),
  ];

  const renderQrStepper = () => (
    <div className="flex items-center justify-center gap-2" aria-hidden>
      {qrStepLabels.map((label, i) => {
        const done = i < qrStepIndex;
        const active = i === qrStepIndex;
        const color = done ? 'var(--status-success)' : active ? 'var(--cta-button-bg)' : 'var(--color-text-muted)';
        return (
          <React.Fragment key={label}>
            {i > 0 && <div style={{ width: 28, height: 2, borderRadius: 1, backgroundColor: done || active ? color : 'var(--color-border)' }} />}
            <div className="flex items-center gap-1.5">
              <div
                className="flex items-center justify-center rounded-full text-[11px] font-bold"
                style={{
                  width: 20,
                  height: 20,
                  backgroundColor: done || active ? color : 'transparent',
                  border: done || active ? 'none' : '1.5px solid var(--color-border)',
                  color: done || active ? '#ffffff' : 'var(--color-text-muted)',
                  transition: 'background-color 0.2s',
                }}
              >
                {done ? <Check size={12} strokeWidth={3} /> : i + 1}
              </div>
              <span className="text-xs font-medium" style={{ color: active ? 'var(--color-text)' : 'var(--color-text-muted)' }}>{label}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  const qrFrameStyle: React.CSSProperties = {
    width: 248,
    height: 248,
    maxWidth: '100%',
    borderRadius: 16,
    border: '1px solid var(--color-border)',
  };

  const renderQrFrame = () => {
    if (qrStep === 'scan') {
      return (
        // key={qrCode}: each rotation remounts → a short fade marks that the code changed.
        <div key={qrCode} className="relative bg-white p-4 shadow-sm flex items-center justify-center overflow-hidden" style={{ ...qrFrameStyle, animation: 'bt-fade-in 240ms ease-out' }}>
          <QRCode
            value={qrCode}
            size={216}
            style={{ height: 'auto', maxWidth: '100%', width: '100%', filter: qrFrozen ? 'blur(6px)' : 'none', opacity: qrFrozen ? 0.35 : 1, transition: 'filter 0.3s, opacity 0.3s' }}
          />
          {qrFrozen && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6 text-center">
              <Loader2 className="animate-spin" size={26} style={{ color: 'var(--cta-button-bg)' }} />
              <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>
                {t('enginesManagement.pairing.qrRefreshingTitle', { defaultValue: 'Getting a fresh code…' })}
              </p>
            </div>
          )}
        </div>
      );
    }
    if (qrStep === 'linking' || qrStep === 'reconnecting') {
      const linking = qrStep === 'linking';
      const accent = linking ? 'var(--status-success)' : 'var(--cta-button-bg)';
      return (
        <div className="flex flex-col items-center justify-center gap-3 px-6 text-center" style={{ ...qrFrameStyle, backgroundColor: 'var(--color-surface-muted)' }}>
          <div className="relative flex items-center justify-center" style={{ width: 56, height: 56 }}>
            <Loader2 className="animate-spin absolute" size={56} strokeWidth={1.5} style={{ color: accent, opacity: 0.55 }} />
            <Smartphone size={22} style={{ color: accent }} />
          </div>
          <p className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            {linking
              ? t('enginesManagement.pairing.qrLinkingTitle', { defaultValue: 'Scanned! Syncing your chats…' })
              : t('enginesManagement.pairing.qrReconnectingTitle', { defaultValue: 'Connecting to WhatsApp…' })}
          </p>
          <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
            {linking
              ? t('enginesManagement.pairing.qrLinkingBody', { defaultValue: 'Keep WhatsApp open on your phone. This can take up to a minute.' })
              : t('enginesManagement.pairing.qrReconnectingBody', { defaultValue: 'If you just scanned, keep WhatsApp open on your phone — this takes a few seconds.' })}
          </p>
        </div>
      );
    }
    // starting
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 text-center" style={{ ...qrFrameStyle, backgroundColor: 'var(--color-surface-muted)' }}>
        <Loader2 className="animate-spin" size={30} style={{ color: 'var(--cta-button-bg)' }} />
        <p className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
          {t('enginesManagement.pairing.qrPreparingTitle', { defaultValue: 'Preparing your QR code…' })}
        </p>
        <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
          {startingSlow
            ? t('enginesManagement.pairing.qrSlowBody', { defaultValue: 'This is taking longer than usual.' })
            : t('enginesManagement.pairing.qrPreparingBody', { defaultValue: 'Setting up your secure connection. This usually takes under a minute.' })}
        </p>
        {startingSlow && (
          <Button variant="outline" size="sm" disabled={restarting} onClick={handleRestart}>
            <RotateCcw className={`h-3.5 w-3.5 me-1.5 ${restarting ? 'animate-spin' : ''}`} />
            {t('enginesManagement.pairing.qrRestart', { defaultValue: 'Restart connection' })}
          </Button>
        )}
      </div>
    );
  };

  const renderQrPanel = () => (
    <div className="flex flex-col items-center gap-5">
      {/* Inline, the host page already shows where the user is — one progress indicator, not two. */}
      {!inline && renderQrStepper()}
      {renderQrFrame()}
      {(qrStep === 'starting' || qrStep === 'scan') && (
        <ol className="w-full max-w-[300px] space-y-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
          {[
            t('enginesManagement.pairing.qrScanStep1', { defaultValue: 'Open WhatsApp on your phone' }),
            t('enginesManagement.pairing.qrScanStep2', { defaultValue: 'Tap Settings → Linked devices → Link a device' }),
            t('enginesManagement.pairing.qrScanStep3', { defaultValue: 'Point your phone at this code' }),
          ].map((line, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span
                className="flex-none flex items-center justify-center rounded-full text-[11px] font-bold"
                style={{ width: 20, height: 20, marginTop: 1, backgroundColor: 'var(--color-surface-accent)', color: 'var(--color-text)' }}
              >
                {i + 1}
              </span>
              <span>{line}</span>
            </li>
          ))}
        </ol>
      )}
      {qrStep === 'scan' && (
        <p className="text-xs -mt-1" style={{ color: 'var(--color-text-muted)' }}>
          {t('enginesManagement.pairing.qrRefreshesHint', { defaultValue: 'The code refreshes automatically — no need to reload.' })}
        </p>
      )}
    </div>
  );

  const keyframes = (
    <style>{`
      @keyframes bt-fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: none; } }
      @keyframes bt-pop-in { 0% { opacity: 0; transform: scale(0.6); } 70% { transform: scale(1.08); } 100% { opacity: 1; transform: none; } }
    `}</style>
  );

  // Method toggle — phone-linking first (the mobile-friendly path). Hidden until the
  // pairing-code bridge ships (PAIRING_CODE_ENABLED); the flow is QR-only until then.
  // Hidden once connected: success is the same screen whichever method got there.
  const methodToggle = PAIRING_CODE_ENABLED && !isConnected && !expired && (
    <div
      className="flex gap-1 rounded-xl p-1"
      style={{ backgroundColor: 'var(--color-surface-muted)', border: '1px solid var(--color-border)' }}
    >
      <button type="button" style={segStyle(method === 'phone')} onClick={() => setMethod('phone')}>
        <Smartphone size={16} style={{ flexShrink: 0 }} />
        <span style={segLabelStyle}>
          {t('enginesManagement.pairing.methodPhone', { defaultValue: 'Link with phone number' })}
        </span>
      </button>
      <button type="button" style={segStyle(method === 'qr')} onClick={() => setMethod('qr')}>
        <QrCode size={16} style={{ flexShrink: 0 }} />
        <span style={segLabelStyle}>
          {t('enginesManagement.pairing.methodQr', { defaultValue: 'Scan QR code' })}
        </span>
      </button>
    </div>
  );

  const secondDeviceHint = PAIRING_CODE_ENABLED && method === 'qr' && isMobile && !isConnected && !expired && (
    <p className="text-xs -mt-2" style={{ color: 'var(--color-text-muted)' }}>
      {t('enginesManagement.pairing.qrNeedsSecondDevice', { defaultValue: 'Scanning needs a second device. On this phone, use “Link with phone number”.' })}
    </p>
  );

  const panel = expired ? renderExpired() : isConnected ? renderConnected() : method === 'phone' ? renderPhonePanel() : renderQrPanel();

  if (inline) {
    // One quiet switch instead of a two-button toggle — the way WhatsApp itself offers it.
    const switchMethod = PAIRING_CODE_ENABLED && !isConnected && !expired && (
      <button type="button" onClick={() => setMethod(method === 'qr' ? 'phone' : 'qr')} className="self-center inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: 'var(--cta-button-bg)', background: 'none', border: 'none', cursor: 'pointer' }}>
        {method === 'qr' ? <Smartphone size={15} /> : <QrCode size={15} />}
        {method === 'qr'
          ? t('enginesManagement.pairing.switchToPhone', { defaultValue: 'Link with phone number instead' })
          : t('enginesManagement.pairing.switchToQr', { defaultValue: 'Scan a QR code instead' })}
      </button>
    );
    return (
      <div className="flex flex-col gap-5">
        {keyframes}
        {expired || !isConnected ? panel : null}
        {secondDeviceHint}
        {switchMethod}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* gap-5 is deliberate: DialogContent stacks its children with NO spacing of its own, so
          without it the header, the method toggle and the panel butt straight into each other. */}
      <DialogContent className="sm:max-w-[460px] max-w-[calc(100vw-24px)] gap-5 sm:p-7">
        {keyframes}
        <DialogHeader className={`pe-8 space-y-2 ${isConnected || expired ? 'sr-only' : ''}`}>
          <DialogTitle className="text-xl">{t('enginesManagement.pairing.dialogTitle', { defaultValue: 'Connect your WhatsApp' })}</DialogTitle>
          <DialogDescription className="leading-relaxed">
            {t('enginesManagement.pairing.dialogSubtitle', { defaultValue: 'Choose how to link this connection to your WhatsApp account.' })}
          </DialogDescription>
        </DialogHeader>
        {methodToggle}
        {secondDeviceHint}
        {panel}
      </DialogContent>
    </Dialog>
  );
};

export default ConnectGatewayDialog;
